import type { ScriptLine } from "../../coldcompute";

// Caption lines are SCENE-LOCAL (fromSec resets to 0 inside each scene's <Sequence>).
// Grounded in EAKC-MVP:
//   RRF      → src/lib/rag/rrf-scoring.ts (RRF_K = 60, Cormack/Clarke/Buettcher 2009)
//   CRAG     → src/lib/rag/quality-gate.ts (Yan et al. 2024; correct/ambiguous/incorrect)
//   1536 dim → src/lib/rag/embeddings.ts:41 (Qwen3-Embedding-8B, Matryoshka → 1536)

export const HOOK: ScriptLine[] = [
  { scene: "hook", fromSec: 0.3, holdSec: 4.4, caption: "Three letters and a number explain most of my RAG quality: RRF, CRAG, and 1536." },
];

export const RRF: ScriptLine[] = [
  { scene: "rrf", fromSec: 0.4, holdSec: 5.5, caption: "RRF — Reciprocal Rank Fusion. I rank chunks five different ways, then merge the rankings." },
  { scene: "rrf", fromSec: 6.2, holdSec: 5.5, caption: "Old system multiplied the signals — one strong signal exploded the score and dominated." },
  { scene: "rrf", fromSec: 12, holdSec: 5.5, caption: "RRF adds a bounded bonus per rank instead. No single signal can run away with it." },
];

export const RRF_CODE: ScriptLine[] = [
  { scene: "rrfCode", fromSec: 0.4, holdSec: 6, caption: "Each signal's contribution is one over sixty plus the rank. Capped at 1/61 ≈ 0.016." },
  { scene: "rrfCode", fromSec: 6.6, holdSec: 6, caption: "k = 60 is the constant from the 2009 Cormack paper. Higher k flattens the rank curve." },
  { scene: "rrfCode", fromSec: 13, holdSec: 5, caption: "A chunk that's good in every signal beats a one-hit wonder that spikes in just one." },
];

export const CRAG: ScriptLine[] = [
  { scene: "crag", fromSec: 0.4, holdSec: 5.5, caption: "CRAG — Corrective RAG. After retrieval, a small LLM grades whether the chunks actually answer." },
  { scene: "crag", fromSec: 6.2, holdSec: 5.5, caption: "It's strict: if you asked about X and the chunks discuss a lookalike Y, the verdict is incorrect." },
  { scene: "crag", fromSec: 12, holdSec: 5.5, caption: "From the CRAG paper, Yan et al. 2024 — adapted to run as a production quality gate." },
];

export const CRAG_FLOW: ScriptLine[] = [
  { scene: "cragFlow", fromSec: 0.4, holdSec: 5.5, caption: "Correct: pass the chunks straight through. The retrieval was already good enough." },
  { scene: "cragFlow", fromSec: 6.2, holdSec: 5.5, caption: "Ambiguous: re-run a broader search with a lower threshold to widen coverage." },
  { scene: "cragFlow", fromSec: 12, holdSec: 4.5, caption: "Next: how I sync 670+ files into vector chunks on a budget VPS." },
];

export const DIMS: ScriptLine[] = [
  { scene: "dims", fromSec: 0.4, holdSec: 5.5, caption: "And 1536 — the embedding dimension. The model is Qwen3-Embedding-8B, #1 on MTEB-Code." },
  { scene: "dims", fromSec: 6.2, holdSec: 5.5, caption: "It's natively flexible via Matryoshka — I truncate to 1536 to fit my pgvector column." },
  { scene: "dims", fromSec: 12, holdSec: 5.5, caption: "Not a magic number — it's the width that balances recall against index size and cost." },
];

export const PAYOFF: ScriptLine[] = [
  { scene: "payoff", fromSec: 0.4, holdSec: 6, caption: "1536-wide vectors get retrieved, five signals fuse with RRF, then CRAG checks the result." },
  { scene: "payoff", fromSec: 6.6, holdSec: 6, caption: "Three independent layers — embed, fuse, verify — each one a small, boring, testable piece." },
];

export const OUTRO: ScriptLine[] = [
  { scene: "outro", fromSec: 0.4, holdSec: 5, caption: "Good RAG isn't one clever trick. It's fusion you can bound, a grader you can trust, dimensions you chose." },
  { scene: "outro", fromSec: 6, holdSec: 4, caption: "Next: HyDE — writing a fake answer to find the real one." },
];

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
